import type { Directive, DirectiveBinding } from 'vue'
import { mask } from 'maska'

/**
 * #2289 — a diretiva global da `maska@1.5.2` grava a máscara no atributo `data-mask` do
 * input (`node_modules/maska/src/maska.js:36-38`). O `jsuites@5.13.5`, trazido pelo
 * `jspreadsheet-ce` (#2269), sequestra qualquer input com esse atributo — a condição exata
 * do handler global dele (`node_modules/jsuites/dist/jsuites.js:13575-13579`) — e reescreve
 * o valor com o próprio motor de máscara, produzindo a máscara literal na tela (`##:##`).
 *
 * Esta diretiva reusa a função pura `mask()` da própria `maska`, mas guarda a máscara em
 * memória (`WeakMap`), nunca em atributo do DOM — elimina a colisão sem tocar no motor de
 * mascaramento em si, então o comportamento visível de cada `v-maska="..."` existente não muda.
 *
 * `fixInputSelection` e o disparo dos eventos `maska`/`input` são portados verbatim de
 * `node_modules/maska/src/utils.js` e `node_modules/maska/src/maska.js` (MIT) para manter o
 * comportamento de caret idêntico ao da v1.
 */

type MaskValue = string | string[]

interface MaskEntry {
  mask: string
  listener: (e: Event) => void
}

const maskState = new WeakMap<HTMLInputElement, MaskEntry>()

// Divergência deliberada de node_modules/maska/src/utils.js:13 (`findInputElement`), que tem o
// fallback `|| el`: aqui, se não houver `<input>` descendente, retornamos `null` em vez do
// próprio `el`. Único caminho afetado hoje: `src/components/designSystem/Inputs/Input.vue` com
// `multiline` (renderiza `<textarea>`, sem `<input>` descendente) combinado com `v-maska`. Sem
// consumidor atual (nenhum call site usa `multiline` com `mask`), então isto não muda
// comportamento nenhum — mas se algum dia alguém combinar os dois, a máscara será um no-op
// silencioso (sem erro, sem log) em vez de aplicar no `<textarea>` como a v1 fazia.
function resolveInput(el: HTMLElement): HTMLInputElement | null {
  return el instanceof HTMLInputElement ? el : el.querySelector('input')
}

/** A v1 serializa array com JSON.stringify; `mask()` faz o JSON.parse para máscara dinâmica. */
function serializeMask(value: MaskValue): string {
  return Array.isArray(value) ? JSON.stringify(value) : value
}

function dispatch(el: HTMLInputElement, name: string) {
  const event = document.createEvent('Event')
  event.initEvent(name, true, true)
  el.dispatchEvent(event)
}

// Porte verbatim de node_modules/maska/src/utils.js:17-29
function fixInputSelection(el: HTMLInputElement, position: number, digit: string | undefined) {
  while (position && position < el.value.length && el.value.charAt(position - 1) !== digit) {
    position++
  }

  const selectionRange = el.type ? /^(text|search|password|tel|url)$/i.test(el.type) : !el.type
  if (selectionRange && el === document.activeElement) {
    el.setSelectionRange(position, position)
    setTimeout(() => el.setSelectionRange(position, position), 0)
  }
}

// Porte de node_modules/maska/src/maska.js:58-90 (updateValue), sem dataset.mask
function applyMask(el: HTMLInputElement, maskValue: string, evt?: Event) {
  if (!el.value) {
    el.dataset.maskRawValue = ''
    dispatch(el, 'maska')
    return
  }

  let position = el.selectionEnd ?? el.value.length
  const oldValue = el.value
  const digit = oldValue[position - 1]

  el.dataset.maskRawValue = mask(el.value, maskValue, undefined, false)
  el.value = mask(el.value, maskValue, undefined, true)

  // Porte de node_modules/maska/src/maska.js:81-83 — sem isto, o caret fica uma posição
  // atrás do dígito recém-digitado sempre que ele completa um grupo antes de um separador
  // (ex.: 2º dígito de `##:##`), porque `fixInputSelection` para no primeiro caractere que
  // bate com `digit`, e esse caractere é o próprio dígito digitado, não o separador após ele.
  if (evt && (evt as InputEvent).inputType === 'insertText' && position === oldValue.length) {
    position = el.value.length
  }

  fixInputSelection(el, position, digit)

  dispatch(el, 'maska')
  if (el.value !== oldValue) {
    dispatch(el, 'input')
  }
}

function bind(el: HTMLElement, binding: DirectiveBinding<MaskValue>) {
  if (!binding.value) return

  const input = resolveInput(el)
  if (!input) return

  const serialized = serializeMask(binding.value)
  const existing = maskState.get(input)
  if (existing && existing.mask === serialized) return

  if (existing) {
    input.removeEventListener('input', existing.listener)
  }

  const listener = (e: Event) => applyMask(input, serialized, e)
  maskState.set(input, { mask: serialized, listener })
  input.addEventListener('input', listener)

  setTimeout(() => applyMask(input, serialized), 0)
}

function unbind(el: HTMLElement) {
  const input = resolveInput(el)
  if (!input) return

  const existing = maskState.get(input)
  if (!existing) return

  input.removeEventListener('input', existing.listener)
  maskState.delete(input)
}

export const vMask: Directive<HTMLElement, MaskValue> = {
  mounted: bind,
  updated: bind,
  unmounted: unbind
}
